/**
 * The ways somebody can prove it is them — `docs/identity-and-sign-in.md` §4.
 *
 * Two-step used to be a boolean and a TOTP secret, which could describe one
 * arrangement: an authenticator app, or nothing. It is a **list** now, and
 * this file is the one place that says what kinds exist, what order they are
 * offered in, what each is called, and who is allowed to skip.
 *
 * Shared because the server decides and the screen draws, and the two must not
 * disagree about which method is recommended or which account may go without.
 */
export const MFA_KINDS = ['passkey', 'totp', 'email', 'sms'];
/**
 * The order every list uses — the setup dialog's four cards, *Try another way*,
 * and the method a sign-in offers first when nothing is remembered.
 *
 * It is strongest first, and it is deliberately not "most recently used". A
 * person who signed in with an email code last week should still be offered
 * their passkey today: the fallback is a fallback.
 */
export const MFA_ORDER = MFA_KINDS;
export const MFA_KIND_INFO = {
    passkey: {
        kind: 'passkey',
        name: 'Passkey',
        blurb: 'Face ID, Windows Hello, Touch ID or a security key. Fastest, and it cannot '
            + 'be phished.',
        recommended: true,
    },
    totp: {
        kind: 'totp',
        name: 'Authenticator app',
        blurb: 'A 6-digit code from Google Authenticator, 1Password, Microsoft Authenticator…',
    },
    email: {
        kind: 'email',
        name: 'Email code',
        blurb: 'A code sent to the address you sign in with. Good as a backup, slow every day.',
    },
    sms: {
        kind: 'sms',
        /*
         * The ranking is in the sentence, not in an amber line beside it. SMS is
         * the one people reach for first and the one that loses to a SIM swap, and
         * board 3 says it plainly in the same words as the other three rather than
         * in a warning colour nobody reads twice. It is offered — somebody with no
         * smartphone and no passkey needs *something* — but never recommended, and
         * §4.2 says never the only method an owner or an admin may hold.
         */
        name: 'Text message',
        blurb: 'A code texted to your mobile. Works anywhere, weakest of the four.',
    },
};
export const MFA_WANTED = 2;
export const mfaPolicy = (required) => ({
    required,
    canSkip: !required,
    wanted: MFA_WANTED,
});
/**
 * `a•••n@koowali.com`, `+1 717 ••• 0142`.
 *
 * Here rather than in the server, so a hint the browser builds for a method it
 * just added reads exactly like the one the server sends at sign-in. Enough to
 * recognise your own address, not enough to learn somebody else's.
 */
export function maskEmail(email) {
    const at = email.lastIndexOf('@');
    if (at < 1)
        return '•••';
    const name = email.slice(0, at);
    const domain = email.slice(at);
    if (name.length <= 2)
        return `${name[0]}•••${domain}`;
    return `${name[0]}•••${name[name.length - 1]}${domain}`;
}
export function maskPhone(phone) {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 4)
        return '•••';
    const last = digits.slice(-4);
    /* Keep the country and area code: it is how somebody tells two phones apart. */
    const lead = digits.length > 7 ? digits.slice(0, digits.length - 7) : '';
    const area = digits.length >= 7 ? digits.slice(-7, -4) : '';
    return `${lead ? `+${lead} ` : ''}${area ? `${area} ` : ''}••• ${last}`.trim();
}
/** The order a person's own methods are listed in. */
export const byMfaOrder = (a, b) => MFA_ORDER.indexOf(a.kind) - MFA_ORDER.indexOf(b.kind);
/**
 * Which panel a sign-in opens on.
 *
 * The remembered one when the person still holds it, the strongest otherwise.
 * `remembered` comes from that browser's `localStorage`, so it is a preference
 * and never a fact the server relies on.
 */
export function preferredMethod(methods, remembered) {
    if (methods.length === 0)
        return null;
    const kept = remembered
        ? methods.find((one) => one.id === remembered || one.kind === remembered)
        : undefined;
    return kept ?? [...methods].sort(byMfaOrder)[0];
}
//# sourceMappingURL=mfa.js.map